//Overview Dashboard//

import React from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, BarChart, Bar, Legend
} from 'recharts';
import {
  Users, Building, FolderOpen, TrendingUp, AlertTriangle, Clock, Calendar,
  Trophy, Award, CheckCircle2
} from 'lucide-react';
import MetricCard from './MetricCard';

const getRankBadgeStyle = (index) => {
  switch (index) {
    case 0:
      return {
        bg: "bg-gradient-to-br from-amber-400 to-yellow-600 shadow-md shadow-yellow-500/20 text-white",
        icon: <Trophy className="w-4.5 h-4.5 text-white animate-pulse" />
      };
    case 1:
      return {
        bg: "bg-gradient-to-br from-slate-300 to-slate-500 shadow-md shadow-slate-400/20 text-white",
        icon: <Award className="w-4.5 h-4.5 text-white" />
      };
    case 2:
      return {
        bg: "bg-gradient-to-br from-amber-600 to-orange-700 shadow-md shadow-orange-600/20 text-white",
        icon: <Award className="w-4.5 h-4.5 text-white" />
      };
    default:
      return {
        bg: "bg-gradient-to-br from-blue-400 to-indigo-600 text-white",
        icon: <span className="text-xs font-extrabold">{index + 1}</span>
      };
  }
};

const MobileMetricCard = ({ title, value, icon, color }) => {
  const colors = {
    blue: {
      border: 'border-t-blue-500',
      bg: 'bg-blue-50/60',
      text: 'text-blue-600',
      glow: 'shadow-blue-500/5'
    },
    green: {
      border: 'border-t-emerald-500',
      bg: 'bg-emerald-50/60',
      text: 'text-emerald-600',
      glow: 'shadow-emerald-500/5'
    },
    orange: {
      border: 'border-t-orange-500',
      bg: 'bg-orange-50/60',
      text: 'text-orange-600',
      glow: 'shadow-orange-500/5'
    },
    purple: {
      border: 'border-t-purple-500',
      bg: 'bg-purple-50/60',
      text: 'text-purple-600',
      glow: 'shadow-purple-500/5'
    }
  }[color] || {
    border: 'border-t-gray-400',
    bg: 'bg-gray-50/60',
    text: 'text-gray-600',
    glow: ''
  };

  return (
    <div className={`bg-white rounded-2xl border-t-4 border-t-blue-500 border border-slate-100 p-4 shadow-sm shadow-blue-500/5 flex flex-col justify-between h-[105px] transition-all active:scale-98`}>
      <div className="flex justify-between items-start">
        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider line-clamp-1">{title}</span>
        <div className={`p-1.5 rounded-lg ${colors.bg} ${colors.text} flex-shrink-0`}>
          {React.cloneElement(icon, { className: 'w-4 h-4' })}
        </div>
      </div>
      <div>
        <span className="text-xl font-extrabold text-slate-800 tracking-tight font-sans block">{value}</span>
      </div>
    </div>
  );
};

const OverviewDashboard = ({ stats, dashboardData, userRole }) => {
  // Format trend values
  const formatTrend = (value) => {
    if (value === 0) return '0%';
    return value > 0 ? `+${value}%` : `${value}%`;
  };

  console.log('📊 OverviewDashboard Received Data:', {
    performanceData: dashboardData.performanceData,
    departmentStats: dashboardData.departmentStats,
    recentTickets: dashboardData.recentTickets,
    recentTicketsLength: dashboardData.recentTickets?.length
  });

  return (
    <div className="space-y-4">
      {/* Key Metrics - Desktop */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {userRole && userRole !== 'company' ? (
          <>
            <MetricCard
              icon={<Users className="w-6 h-6" />}
              title="My Total Tasks"
              value={stats.totalEmployees}
              color="blue"
            />
            <MetricCard
              icon={<TrendingUp className="w-6 h-6" />}
              title="Tasks Completed"
              value={stats.totalCustomers}
              color="green"
            />
            <MetricCard
              icon={<FolderOpen className="w-6 h-6" />}
              title="My Projects"
              value={stats.activeProjects}
              color="orange"
            />
            <MetricCard
              icon={<Calendar className="w-6 h-6" />}
              title="My Attendance"
              value={`${stats.attendanceRate}%`}
              color="purple"
            />
          </>
        ) : (
          <>
            <MetricCard
              icon={<Users className="w-6 h-6" />}
              title="Total Employees"
              value={stats.totalEmployees}
              color="blue"
            />
            <MetricCard
              icon={<Building className="w-6 h-6" />}
              title="Total Customers"
              value={stats.totalCustomers}
              color="green"
            />
            <MetricCard
              icon={<FolderOpen className="w-6 h-6" />}
              title="Active Projects"
              value={stats.activeProjects}
              color="orange"
            />
            <MetricCard
              icon={<TrendingUp className="w-6 h-6" />}
              title="Attendance Rate"
              value={`${stats.attendanceRate}%`}
              color="purple"
            />
          </>
        )}
      </div>

      {/* Key Metrics - Mobile */}
      <div className="grid grid-cols-2 gap-3.5 md:hidden">
        {userRole && userRole !== 'company' ? (
          <>
            <MobileMetricCard
              icon={<Users className="w-4 h-4" />}
              title="My Total Tasks"
              value={stats.totalEmployees}
              color="blue"
            />
            <MobileMetricCard
              icon={<TrendingUp className="w-4 h-4" />}
              title="Tasks Completed"
              value={stats.totalCustomers}
              color="green"
            />
            <MobileMetricCard
              icon={<FolderOpen className="w-4 h-4" />}
              title="My Projects"
              value={stats.activeProjects}
              color="orange"
            />
            <MobileMetricCard
              icon={<Calendar className="w-4 h-4" />}
              title="My Attendance"
              value={`${stats.attendanceRate}%`}
              color="purple"
            />
          </>
        ) : (
          <>
            <MobileMetricCard
              icon={<Users className="w-4 h-4" />}
              title="Total Employees"
              value={stats.totalEmployees}
              color="blue"
            />
            <MobileMetricCard
              icon={<Building className="w-4 h-4" />}
              title="Total Customers"
              value={stats.totalCustomers}
              color="green"
            />
            <MobileMetricCard
              icon={<FolderOpen className="w-4 h-4" />}
              title="Active Projects"
              value={stats.activeProjects}
              color="orange"
            />
            <MobileMetricCard
              icon={<TrendingUp className="w-4 h-4" />}
              title="Attendance Rate"
              value={`${stats.attendanceRate}%`}
              color="purple"
            />
          </>
        )}
      </div>

      {/* Upcoming Deadlines */}


      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* TOP TASKS PERFORMERS — Desktop (Original Style) */}
        <div className="hidden md:block bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Tasks Performers</h3>
          {dashboardData.performanceData.length > 0 ? (
            <div className="space-y-4">
              {dashboardData.performanceData.map((employee, index) => (
                <div key={employee.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{employee.name}</p>
                      <p className="text-sm text-gray-500">{employee.role} - {employee.completed} items completed</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">No performance data available</div>
          )}
        </div>

        {/* TOP TASKS PERFORMERS — Mobile (Premium Redesign) */}
        <div className="block md:hidden bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-slate-800 tracking-tight font-sans">
                Top Tasks Performers
              </h3>
              <p className="text-[10px] text-slate-400 mt-0.5">Leading team members by completed tasks</p>
            </div>
            <div className="p-1.5 bg-amber-50 rounded-lg border border-amber-100">
              <Trophy className="w-4 h-4 text-amber-500" />
            </div>
          </div>

          {dashboardData.performanceData.length > 0 ? (
            <div className="space-y-2.5">
              {dashboardData.performanceData.map((employee, index) => {
                const badgeStyle = getRankBadgeStyle(index);
                return (
                  <div 
                    key={employee.name} 
                    className="group flex items-center justify-between p-3 bg-slate-50/50 rounded-xl border border-slate-100 hover:border-blue-100 transition-all duration-300"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      {/* Rank Badge */}
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${badgeStyle.bg}`}>
                        {badgeStyle.icon}
                      </div>
                      
                      <div className="min-w-0">
                        <div className="flex items-center space-x-1.5">
                          <span className="font-bold text-slate-700 text-sm truncate">
                            {employee.name}
                          </span>
                          <span className="text-[8px] font-semibold bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                            {employee.role || 'Member'}
                          </span>
                        </div>
                        <p className="text-[11px] text-slate-400 mt-0.5 flex items-center font-sans">
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mr-1" />
                          {employee.completed} {employee.completed === 1 ? 'item' : 'items'} completed
                        </p>
                      </div>
                    </div>

                    {employee.completed > 0 && (
                      <div className="flex-shrink-0">
                        <span className="inline-flex items-center justify-center text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-100/50 px-1.5 py-0.5 rounded">
                          Active
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-slate-400">
              <Award className="w-10 h-10 mx-auto mb-2 text-slate-300 stroke-1" />
              <p className="text-xs font-medium">No performance data available</p>
            </div>
          )}
        </div>

        {/* UPCOMING DEADLINES — moved here from Row 1 left */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          {dashboardData.upcomingDeadlines.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Deadlines</h3>
              <div>
                {dashboardData.upcomingDeadlines.map((item, index) => {
                  const deadline = new Date(item.dueDate);
                  const today = new Date();
                  const daysUntil = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="flex items-center space-x-3">
                        {daysUntil <= 1 ? <AlertTriangle className="w-5 h-5 text-red-500" /> : daysUntil <= 3 ? <Clock className="w-5 h-5 text-yellow-500" /> : <Calendar className="w-5 h-5 text-blue-500" />}
                        <div>
                          <h4 className="font-medium text-gray-900">{item.title || item.name || item.projectName}</h4>
                          <p className="text-sm text-gray-500">{item.type} - {daysUntil === 0 ? 'Due today' : `Due in ${daysUntil} days`}</p>
                        </div>
                      </div>
                      <span className="text-sm text-gray-400">{deadline.toLocaleDateString('en-GB')}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

      </div>

      {/* Performance & Department Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* PROJECT STATUS DISTRIBUTION — moved here from Row 1 right */}
        <div className="bg-white rounded-2xl border-t-4 border-t-blue-500 border border-slate-100 md:border-0 md:border-t-0 p-6 shadow-sm shadow-blue-500/5 md:shadow-sm md:rounded-xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Status Distribution</h3>
          {dashboardData.projectStatus.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={dashboardData.projectStatus}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {dashboardData.projectStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value, name) => [`${value}`, name]} />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  formatter={(value, entry) => `${value} (${entry.payload.value})`}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">No project data available</div>
          )}
        </div>

        {/* Recent Tickets — unchanged */}
        <div className="bg-white rounded-2xl border-t-4 border-t-blue-500 border border-slate-100 md:border-0 md:border-t-0 p-6 shadow-sm shadow-blue-500/5 md:shadow-sm md:rounded-xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Tickets</h3>
          {dashboardData.recentTickets && dashboardData.recentTickets.length > 0 ? (
            <div className="space-y-2 max-h-[350px] overflow-y-auto">
              {dashboardData.recentTickets.slice(0, 4).map((ticket, index) => (
                <div key={ticket._id || index} className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors hover:shadow-sm">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h4 className="font-semibold text-gray-900 text-sm line-clamp-1 flex-1">{ticket.subject || "No Subject"}</h4>
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full whitespace-nowrap ${ticket.status === 'open' ? 'bg-blue-100 text-blue-800' :
                      ticket.status === 'inprogress' ? 'bg-yellow-100 text-yellow-800' :
                        ticket.status === 'resolved' ? 'bg-green-100 text-green-800' :
                          ticket.status === 'closed' ? 'bg-gray-300 text-gray-700' :
                            'bg-orange-100 text-orange-800'
                      }`}>
                      {ticket.status || 'Unknown'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <div className="flex items-center text-xs text-gray-600 flex-1 min-w-0">
                      <Users className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{ticket.customerName || "Unknown"}</span>
                    </div>
                    <div className="flex items-center text-xs text-gray-600 flex-1 min-w-0">
                      <Building className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{ticket.department || "N/A"}</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full ${ticket.priority === 'high' ? 'bg-red-100 text-red-800' :
                      ticket.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'
                      }`}>
                      {ticket.priority ? ticket.priority.toUpperCase() : 'LOW'}
                    </span>
                    <span className="text-[10px] text-gray-400">
                      {ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'N/A'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-64 flex flex-col items-center justify-center text-gray-500">
              <FolderOpen className="w-12 h-12 mb-2 text-gray-300" />
              <p className="text-sm">No recent tickets available</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default OverviewDashboard;
